// ============================================================================
// Feedback create form — modal dialog overlaid on Facility Detail.
// Writes a new row to facility_feedback. The FBK-XXXX feedback_id is assigned
// by the BEFORE INSERT trigger `trg_facility_feedback_assign_id` (added in
// migration 20260512_facility_feedback_id_default.sql); the client omits it.
// RLS / authenticated context determines who can write.
//
// ⭐ A COMPLAINT NOTIFIES THE ACCOUNT MANAGER BY ITSELF (2026-09-03, TCSD-0177).
// Saving a Negative or Critical record notifies that building's AM — from a
// database trigger, notify_src_facility_feedback(), not from this file. Positive
// and Neutral stay silent, and nobody is notified about a record they wrote
// themselves. No task is created: an auto-task is an obligation someone must
// close, and that is how the My Work backlog reached 1,283 rows.
//
// ⛔ THE OPT-IN CHECKBOX THAT USED TO LIVE HERE IS GONE, and it is not a loss —
// it emitted ZERO notifications in its whole life. Do not add a manual switch
// back, and do not add a second notify path in this file.
// ============================================================================

import { useState, type FormEvent } from 'react';
import { useAuth } from '../../shared/auth';
import { feedbackFlagDefaults, saveFeedback } from './lib/building-saves';
import type { Facility, FeedbackSentiment } from '../../shared/types';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';
import { localToday } from '../../shared/format';

interface Props {
  facility: Facility;
  onClose: () => void;
  onSaved: () => void;
}

// ⛔ SOURCES and STATUSES were the option lists for two <select>s that came off
// this form on 2026-09-03 (addendum 2D). Removed with them rather than left
// behind: an unused list of legal values is the thing someone later mistakes
// for the vocabulary and rebuilds a picker from.
const SENTIMENTS: FeedbackSentiment[] = [
  'Positive',
  'Neutral',
  'Negative',
  'Critical',
];

export default function FeedbackForm({ facility, onClose, onSaved }: Props) {
  const { employee } = useAuth();
  const [sentiment, setSentiment] = useState<FeedbackSentiment>('Neutral');
  // ⛔ OFF THE FORM 2026-09-03, Ben (addendum 2D) — Category, Source, Status and
  // Issue count are no longer asked. The COLUMNS stay, and so do these VALUES:
  // the form still sends exactly what it sent before, it just stops making the
  // AM choose. Same treatment the project entry screen gave special_pay.
  //
  // ⚠️ `status` IS NOT COSMETIC AND IS NOT DROPPED. Three live surfaces read it —
  // the My Accounts sentiment/RAG rollup, the buildings overview, and the
  // copilot's close-feedback action, which writes 'Resolved' over it. Sending
  // NULL instead of 'Open' would leave every new complaint in a state that is
  // neither open nor resolved, invisible to all three. Removing the QUESTION is
  // safe; removing the VALUE would not be.
  //
  // ⚠️ 177 of 524 rows already hold the Category default and 205 the Source
  // default, so for roughly a third of the book these boxes were never really
  // answered — which is the argument for taking them off.
  // The values themselves: FEEDBACK_FORM_CONSTANTS in lib/building-saves.ts
  // (category Cleaning_Quality, source AM_Observation, status Open, issue
  // count 1), sent by saveFeedback on every save from this form and Ask AI.
  // Today on the person's own device — the UTC day was already tomorrow after
  // ~7 pm Central (item 944).
  const [feedbackDate, setFeedbackDate] = useState(() => localToday());
  const [description, setDescription] = useState('');
  // Issue count: 17 of 524 rows ever carried anything but 1, and the column
  // defaults to 1 — saveFeedback sends 1.
  // D2 — incentive discretion, sentiment-defaulted but always editable.
  const [countsAgainstNmi, setCountsAgainstNmi] = useState(false);
  const [countsTowardCompliment, setCountsTowardCompliment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    setSubmitting(true);
    try {
      // The form's save is lib/building-saves.ts saveFeedback — the same door
      // Ask AI's building card uses (item 944). It sends the category, source,
      // status and issue count below exactly as this form always has, keeps the
      // `.select()` (TCSD-0168) and throws when the row did not land.
      await saveFeedback({
        facility_id: facility.facility_id,
        account_id: facility.account_id,
        feedback_date: feedbackDate,
        reported_by_employee_id: employee?.employee_id ?? null,
        sentiment,
        description,
        counts_against_nmi: countsAgainstNmi,
        counts_toward_compliment: countsTowardCompliment,
      });

      // ⛔ NO ESCALATION CALL HERE ANY MORE. The complaint notification is a
      // DATABASE TRIGGER now (notify_src_facility_feedback), which means it also
      // covers the copilot's feedback card and any other insert path — the
      // client-side call never did. It raises a WARNING and never fails the
      // insert: recording what a client said must not depend on the notify.
      onSaved();
    } catch (e) {
      console.error('[feedback-form] insert failed', e);
      setError(errorMessage(e, 'Could not save feedback.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full max-w-lg rounded-lg bg-tcs-overlay p-5 shadow-xl"
      >
        <h2 className="text-lg font-semibold text-tcs-text">
          New feedback — {facility.facility_name}
        </h2>
        <p className="mt-1 text-xs text-tcs-text-muted">{facility.facility_id}</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label="Date">
            <input
              type="date"
              value={feedbackDate}
              onChange={(e) => setFeedbackDate(e.target.value)}
              className={inputClass}
              required
            />
          </Field>
          <Field label="Sentiment">
            <select
              value={sentiment}
              onChange={(e) => {
                const next = e.target.value as FeedbackSentiment;
                setSentiment(next);
                // Pre-check the incentive flags from sentiment; AM can still toggle.
                const d = feedbackFlagDefaults(next);
                setCountsAgainstNmi(d.counts_against_nmi);
                setCountsTowardCompliment(d.counts_toward_compliment);
              }}
              className={inputClass}
            >
              {SENTIMENTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          {/* ⛔ Category · Source · Status · Issue count came OFF this form on
              2026-09-03 (Ben, addendum 2D). Four questions between an AM and
              writing down what a client said. The values are still sent — see
              the constants at the top, and the note there about why `status`
              in particular is sent and not simply dropped. */}
        </div>

        <div className="mt-3 space-y-2 rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Performance payment impact
          </p>
          <label className="flex items-start gap-2 text-sm text-tcs-text-secondary">
            <input
              type="checkbox"
              checked={countsAgainstNmi}
              onChange={(e) => setCountsAgainstNmi(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-tcs-border"
            />
            <span>
              Counts against this month&rsquo;s Scope Performance Payment
              <span className="block text-[11px] text-tcs-text-muted">
                Pre-checked for Negative/Critical. A complaint only disqualifies
                the Scope Performance Payment if this is checked.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-tcs-text-secondary">
            <input
              type="checkbox"
              checked={countsTowardCompliment}
              onChange={(e) => setCountsTowardCompliment(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-tcs-border"
            />
            <span>
              Counts toward the quarterly Compliment
              <span className="block text-[11px] text-tcs-text-muted">
                Pre-checked for Positive feedback.
              </span>
            </span>
          </label>
        </div>

        {/* ⛔ THE FOLLOW-UP CHECKBOX IS GONE — Ben, decision 68, option 1,
            2026-09-03. It was the only escalation path on this form and it
            notified NOBODY: zero feedback_follow_up_requested notifications were
            emitted in its entire life, and all 310 Feedback_Auto tasks were
            written on one day by a script with created_by NULL. Not one came
            from a person ticking it.

            A Negative or Critical record now notifies the building's account
            manager AUTOMATICALLY — notify_src_facility_feedback(), a trigger, so
            nobody has to remember anything. Melanie asked for exactly that.
            ⛔ Do not re-add a manual switch here without superseding TCSD-0177. */}

        <Field label="Description" className="mt-3">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={`${inputClass} min-h-[88px] resize-y`}
            placeholder="What happened, where, who reported it…"
            required
          />
        </Field>

        {error && (
          <div className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-sm text-st-danger-fg-strong">
            {error}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-1.5 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
          >
            {submitting ? 'Saving…' : 'Save feedback'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm focus:border-tcs-border-input focus:outline-none';

function Field({
  label,
  children,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block text-xs ${className}`}>
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
